import axios from 'axios'
import { message as antdMessage } from 'antd'
import { API_BASE_URL, STORAGE_KEYS } from '../utils/constants'
import authService from './authService'

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Token refresh state management
let isRefreshing = false
let refreshPromise = null

// Request interceptor - Add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN)
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor - Handle unified response format
api.interceptors.response.use(
  (response) => {
    const result = response.data

    // Check if response follows the unified format
    if (result && typeof result.code !== 'undefined') {
      // Code 0 means success
      if (result.code === 0) {
        // Return the data directly for easier usage
        response.data = result.data
        return response
      } else {
        // Business error (code !== 0)
        const errorMessage = result.message || 'An error occurred'

        // Handle specific error codes
        if (result.code === 1004 || result.code === 1005) {
          // Only show error for auth-related errors
          antdMessage.error(errorMessage)
          localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN)
          localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN)
          localStorage.removeItem(STORAGE_KEYS.ID_TOKEN)
          localStorage.removeItem(STORAGE_KEYS.USER_INFO)
          window.location.href = '/login'
        }

        // Create error with code and message (don't show message here, let component handle it)
        const error = new Error(errorMessage)
        error.code = result.code
        error.result = result
        error.response = response
        return Promise.reject(error)
      }
    }

    // If not unified format, return as is
    return response
  },
  async (error) => {
    const originalRequest = error.config

    // Handle 401 Unauthorized with token refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true

      try {
        // Use singleton pattern to avoid multiple concurrent refresh requests
        if (!isRefreshing) {
          isRefreshing = true
          refreshPromise = authService.refreshAccessToken()
            .finally(() => {
              isRefreshing = false
              refreshPromise = null
            })
        }

        // Wait for token refresh to complete
        const newToken = await refreshPromise

        // Update the failed request with new token
        originalRequest.headers.Authorization = `Bearer ${newToken}`

        // Retry the original request
        return api(originalRequest)
      } catch (refreshError) {
        // Token refresh failed, clear auth and redirect to login
        authService.clearAuth()
        window.location.href = '/login'
        return Promise.reject(refreshError)
      }
    }

    // Handle other errors
    if (error.response) {
      // Server responded with error status
      const { status, data } = error.response

      // Try to extract message from unified format
      let errorMessage = 'An error occurred'

      if (data && typeof data.code !== 'undefined') {
        // Unified error format
        errorMessage = data.message || errorMessage

        // Show validation errors if present
        if (data.code === 422 && data.data && typeof data.data === 'object') {
          const fieldErrors = Object.values(data.data).join(', ')
          errorMessage = fieldErrors || errorMessage
        }
      } else {
        // Legacy error format
        errorMessage = data?.message || data?.error || error.message
      }

      // Don't show error notification here - let components handle display

      // Handle 403 Forbidden (403 doesn't trigger refresh, user doesn't have permission)
      if (status === 403) {
        authService.clearAuth()
        window.location.href = '/login'
      }

      // Create enhanced error object
      const enhancedError = new Error(errorMessage)
      enhancedError.status = status
      enhancedError.code = data?.code
      enhancedError.response = error.response
      return Promise.reject(enhancedError)
    } else if (error.request) {
      // Request made but no response received
      const errorMessage = 'No response from server. Please check your connection.'
      antdMessage.error(errorMessage)
      return Promise.reject(new Error(errorMessage))
    } else {
      // Something else happened
      const errorMessage = error.message || 'An unexpected error occurred'
      antdMessage.error(errorMessage)
      return Promise.reject(new Error(errorMessage))
    }
  }
)

// Task Messages API
export const taskMessagesAPI = {
  /**
   * Get task messages
   * @param {string} taskId - Task ID
   * @param {string} lastTimestamp - Last timestamp (optional for incremental fetch)
   * @returns {Promise<Array>} - Array of messages
   */
  getTaskMessages: (taskId, lastTimestamp = null) => {
    const params = lastTimestamp ? { lastTimestamp } : {}
    return api.get(`/api/v1/tasks/${taskId}/messages`, { params })
  }
}

export default api
